// =============================================================================
// StaffDirectory.jsx — Resources → Staff List (read-only)
// =============================================================================
// A read-only directory of Zephyr staff for all internal users. Shows only staff
// who have opted in (profiles.show_on_staff_list) and are active. Name / Mobile /
// Email / Role, with the staff's colour swatch. No editing here — staff records
// are managed under Manage → Zephyr Staff.
// =============================================================================

import React from "react";
import { listStaffDirectory } from "../../data/profiles";
import { usePageSize } from "../../hooks/usePageSize";

const ROLE_LABELS = {
  staff: "Responder",
  picker: "Picker",
  admin: "Supervisor",
  superuser: "SuperUser",
  combined: "Combined",
};

// Same role badge colours as the admin Zephyr Staff list (keep them in step).
const ROLE_COLOURS = {
  superuser: "#1A237E", // deep indigo
  admin: "#00695C", // Supervisor — teal
  picker: "#E65100", // Picker — orange
  combined: "#9C27B0", // Combined — purple
  staff: "#D32F2F", // Responder — red
};
function roleColour(r) {
  return ROLE_COLOURS[r] || "#5C6BC0";
}

// The admin tier splits by capability (John, 6 Sep 2026): an admin who holds the
// "monitored_approval" matrix capability shows as "Supervisor"; every other admin
// shows as "Admin". All other roles use ROLE_LABELS unchanged.
function roleLabel(u) {
  if (u.role === "admin") {
    const perms = Array.isArray(u.admin_permissions) ? u.admin_permissions : [];
    return perms.includes("monitored_approval") ? "Supervisor" : "Admin";
  }
  return ROLE_LABELS[u.role] || u.role;
}

function staffName(u) {
  return (
    [u.staff_first_name, u.staff_last_name].filter(Boolean).join(" ") ||
    u.display_name ||
    u.staff_user_email ||
    "—"
  );
}

export function StaffDirectory() {
  const [rows, setRows] = React.useState(null);
  const [page, setPage] = React.useState(0);

  React.useEffect(function () {
    listStaffDirectory().then(function (r) {
      setRows(r || []);
    });
  }, []);

  const cols = "24px 1fr 160px 260px 120px";
  const [PAGE_SIZE] = usePageSize();
  const totalPages = Math.max(1, Math.ceil((rows ? rows.length : 0) / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows ? rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE) : [];

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <h1 className="m-0 mb-5 text-[22px] text-zephyr-text">Staff List</h1>
      {rows === null ? (
        <div className="text-zephyr-muted p-5">Loading…</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid gap-0 bg-zephyr-teal-dark text-white py-[10px] px-[18px] text-[11px] font-bold uppercase tracking-[0.5px]"
            style={{ gridTemplateColumns: cols }}
          >
            <div />
            <div>Name</div>
            <div>Mobile</div>
            <div>Email</div>
            <div>Role</div>
          </div>
          {pageRows.map(function (u, idx) {
            return (
              <div
                key={u.id}
                className="grid gap-0 py-[10px] px-[18px] items-center text-[13px]"
                style={{
                  gridTemplateColumns: cols,
                  borderBottom: idx < pageRows.length - 1 ? "1px solid #CFE0DE" : "none",
                  background: idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                }}
              >
                <div
                  title={u.staff_colour || ""}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: u.staff_colour || "transparent",
                    border: u.staff_colour ? "none" : "1px solid #CFE0DE",
                  }}
                />
                <div className="font-semibold text-zephyr-text">{staffName(u)}</div>
                <div className="text-zephyr-muted">{u.staff_mobile || "—"}</div>
                <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {u.staff_user_email ? (
                    <a
                      href={`mailto:${u.staff_user_email}`}
                      className="text-zephyr-teal-dark underline hover:no-underline"
                    >
                      {u.staff_user_email}
                    </a>
                  ) : (
                    <span className="text-zephyr-muted">—</span>
                  )}
                </div>
                <div>
                  <span
                    className="text-[11px] font-bold text-white rounded px-2 py-[2px]"
                    style={{ background: roleColour(u.role) }}
                  >
                    {roleLabel(u)}
                  </span>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="p-10 text-center text-zephyr-muted text-[13px]">
              No staff to show yet.
            </div>
          )}
        </div>
      )}
      {rows !== null && totalPages > 1 && (
        <div className="flex gap-[6px] items-center justify-center mt-4 flex-wrap">
          <button
            type="button"
            onClick={function () {
              setPage(Math.max(0, safePage - 1));
            }}
            disabled={safePage === 0}
            className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, function (_, i) {
            return i;
          }).map(function (i) {
            const active = i === safePage;
            return (
              <button
                key={i}
                type="button"
                onClick={function () {
                  setPage(i);
                }}
                className={`border rounded-[6px] px-[11px] py-[4px] cursor-pointer text-[13px] ${
                  active
                    ? "bg-zephyr-teal-dark border-zephyr-teal-dark text-white font-bold"
                    : "bg-white border-zephyr-border text-zephyr-text"
                }`}
              >
                {i + 1}
              </button>
            );
          })}
          <button
            type="button"
            onClick={function () {
              setPage(Math.min(totalPages - 1, safePage + 1));
            }}
            disabled={safePage >= totalPages - 1}
            className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
